import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormikContext } from "formik";
import { constants } from "@qgiv/core-js";
import { AutomatedEmployerMatching } from "@qgiv/core-donor";
import { selectConfig } from "../../../redux/slices/configSettingsSlice";
import {
    selectGiftDetails,
    selectDonorDetails,
    updateDonationMatching,
} from "../../../redux/slices/donationDetailsSlice";
import { selectAllFormSettings } from "../../../redux/slices/formSettingsSlice";
import { selectMatchingGiftSettings } from "../../../redux/slices/donationSettingsSlice";
import { selectCmsControlById } from "../../../redux/slices/cmsSettingsSlice";
import { selectAllSystemFields } from "../../../redux/slices/fieldsSlice";

/**
 * @param {object} props
 * @param {number} props.controlId
 * @returns {React.JSX.Element}
 */
const ConnectedAutomatedEmployerMatching = ({ controlId }) => {
    const {
        ENUMS: {
            EntityType: { SYSTEM_FIELD_GROUP_MATCHING_GIFTS },
            FieldType,
            GiftMatchingService,
        },
    } = constants;

    const dispatch = useDispatch();

    const updateReduxValues = (values) => {
        dispatch(updateDonationMatching(values));
    };

    const { subtotal } = useSelector(selectGiftDetails);

    const {
        enableMatching,
        thirdPartyMatching,
        providerLogoSrc = "/resources/core/images/logo-doubledonation.png",
        providerLabel = "Double The Donation",
    } = useSelector(selectMatchingGiftSettings);

    const { currency } = useSelector(selectAllFormSettings);

    // default grey spinner color
    const spinnerColor = "#66737D";

    const isDtD =
        thirdPartyMatching === GiftMatchingService.DOUBLE_THE_DONATION;

    const { path } = useSelector(selectConfig);

    const { options } = useSelector((state) =>
        selectCmsControlById(state, controlId),
    );

    const donorDetails = useSelector(selectDonorDetails);
    const { Company_Donation } = donorDetails;

    const {
        sectionHeading = "Your gift could be worth more!",
        description = "Find out if you’re eligible for a matching gift from your employer.",
    } = options;

    const allFieldsAndGroups = useSelector(selectAllSystemFields);

    const matchingGroupData = allFieldsAndGroups.find(
        (field) => Number(field.type) === SYSTEM_FIELD_GROUP_MATCHING_GIFTS,
    );
    const matchingGroupFields = matchingGroupData.fields;

    const employerFieldData = matchingGroupFields.find(
        (field) => Number(field.type) === FieldType.EMPLOYER,
    );

    const matchingEmployerLabel = employerFieldData?.display_text ?? "Employer";
    const matchingEmployerHelpText = employerFieldData?.helpText;

    const { values, setValues } = useFormikContext();
    const { Employer } = values;

    const searchEnabled = !!(Employer && Employer.length > 1);

    const employerField = Employer;

    const displayMatchingGift = enableMatching && !Company_Donation;

    // Donation total without Gift Assist
    const donationTotal = subtotal;

    const isVisible = true;

    const matchingGiftProps = {
        currency,
        displayMatchingGift,
        donationTotal,
        employerField,
        isStandardDonationForm: true,
        isDtD,
        isVisible,
        sectionHeading,
        description,
        matchingEmployerLabel,
        matchingEmployerHelpText,
        noLeftGutter: true,
        path,
        providerLogoSrc,
        providerLabel,
        searchEnabled,
        teaserLabel: "",
        updateReduxValues,
        values,
        setValues,
        spinnerColor,
    };

    return <AutomatedEmployerMatching {...matchingGiftProps} />;
};

export default ConnectedAutomatedEmployerMatching;
